import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { announcementAPI, faqAPI } from '../services/api';
import { Announcement, FAQ } from '../types';
import './SiteMap.css';

interface SiteMapSection {
  title: string;
  icon: string;
  links: {
    title: string;
    path: string;
    description?: string;
  }[];
}

const SiteMap: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDynamicContent();
  }, []);

  const fetchDynamicContent = async () => {
    try {
      setLoading(true);
      const [announcementsRes, faqsRes] = await Promise.all([
        announcementAPI.getAll(),
        faqAPI.getAll()
      ]);
      setAnnouncements(announcementsRes.data.announcements.slice(0, 5)); // Son 5 duyuru
      setFaqs(faqsRes.data.faqs.slice(0, 5)); // İlk 5 SSS
    } catch (error) {
      console.error('İçerik yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const staticSections: SiteMapSection[] = [
    {
      title: 'Ana Sayfalar',
      icon: 'bi-house-door',
      links: [
        { title: 'Hakkımızda', path: '/', description: 'Hukuk büromuz hakkında bilgi' },
        { title: 'Çalışma Alanlarımız', path: '/calisma-alanlarimiz', description: 'Hizmet verdiğimiz hukuk alanları' }
      ]
    },
    {
      title: 'İçerik Sayfaları',
      icon: 'bi-file-text',
      links: [
        { title: 'Duyurular', path: '/announcements', description: 'Tüm duyurular ve haberler' },
        { title: 'Galeri', path: '/gallery', description: 'Fotoğraf galerisi' },
        { title: 'SSS', path: '/faq', description: 'Sıkça sorulan sorular' },
        { title: 'Site Haritası', path: '/sitemap', description: 'Bu sayfa - tüm sayfaların listesi' }
      ]
    },
    {
      title: 'Kullanıcı İşlemleri',
      icon: 'bi-person',
      links: [
        { title: 'Giriş Yap', path: '/login', description: 'Kullanıcı girişi' },
        { title: 'Kayıt Ol', path: '/register', description: 'Yeni kullanıcı kaydı' },
        { title: 'Admin Panel', path: '/admin', description: 'Yönetim paneli (Yetki gerektirir)' }
      ]
    }
  ];

  return (
    <>
      <section className="screen screen-blog">
        <div className="panel-content">
          <h1>Site Haritası</h1>
          <p>Tüm sayfalarımıza hızlı erişim</p>
        </div>
      </section>

      <section className="screen-tail screen-tail--blog" aria-label="Site haritası içerik alanı">
        <div className="tail-container">
          <div className="sitemap-container">
            {/* Static Sections */}
            {staticSections.map((section, index) => (
              <div key={index} className="sitemap-section">
                <h2 className="sitemap-section-title">
                  <i className={`bi ${section.icon}`}></i>
                  {section.title}
                </h2>
                <ul className="sitemap-links">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex} className="sitemap-link-item">
                      <Link to={link.path} className="sitemap-link">
                        <div className="sitemap-link-content">
                          <i className="bi bi-arrow-right-circle"></i>
                          <div className="sitemap-link-info">
                            <span className="sitemap-link-title">{link.title}</span>
                            {link.description && (
                              <span className="sitemap-link-description">{link.description}</span>
                            )}
                          </div>
                        </div>
                        <i className="bi bi-chevron-right sitemap-arrow"></i>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Dynamic Announcements Section */}
            {!loading && announcements.length > 0 && (
              <div className="sitemap-section">
                <h2 className="sitemap-section-title">
                  <i className="bi bi-megaphone"></i>
                  Son Duyurular
                </h2>
                <ul className="sitemap-links">
                  {announcements.map((announcement) => (
                    <li key={announcement.id} className="sitemap-link-item">
                      <Link to={`/announcements/${announcement.slug}`} className="sitemap-link">
                        <div className="sitemap-link-content">
                          <i className="bi bi-arrow-right-circle"></i>
                          <div className="sitemap-link-info">
                            <span className="sitemap-link-title">{announcement.title}</span>
                            <span className="sitemap-link-description">
                              {new Date(announcement.created_at).toLocaleDateString('tr-TR')}
                            </span>
                          </div>
                        </div>
                        <i className="bi bi-chevron-right sitemap-arrow"></i>
                      </Link>
                    </li>
                  ))}
                  <li className="sitemap-link-item">
                    <Link to="/announcements" className="sitemap-link sitemap-link-all">
                      <span>Tüm Duyuruları Gör</span>
                      <i className="bi bi-arrow-right"></i>
                    </Link>
                  </li>
                </ul>
              </div>
            )}

            {/* Dynamic FAQ Section */}
            {!loading && faqs.length > 0 && (
              <div className="sitemap-section">
                <h2 className="sitemap-section-title">
                  <i className="bi bi-question-circle"></i>
                  Popüler SSS
                </h2>
                <ul className="sitemap-links">
                  {faqs.map((faq) => (
                    <li key={faq.id} className="sitemap-link-item">
                      <Link to="/faq" className="sitemap-link">
                        <div className="sitemap-link-content">
                          <i className="bi bi-arrow-right-circle"></i>
                          <div className="sitemap-link-info">
                            <span className="sitemap-link-title">{faq.question}</span>
                            {faq.category && (
                              <span className="sitemap-link-description">Kategori: {faq.category}</span>
                            )}
                          </div>
                        </div>
                        <i className="bi bi-chevron-right sitemap-arrow"></i>
                      </Link>
                    </li>
                  ))}
                  <li className="sitemap-link-item">
                    <Link to="/faq" className="sitemap-link sitemap-link-all">
                      <span>Tüm SSS'leri Gör</span>
                      <i className="bi bi-arrow-right"></i>
                    </Link>
                  </li>
                </ul>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="sitemap-loading">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="sitemap-info">
              <i className="bi bi-info-circle"></i>
              <div className="sitemap-info-content">
                <h3>Site Haritası Hakkında</h3>
                <p>
                  Bu sayfa, web sitemizde bulunan tüm sayfaların organize edilmiş bir listesini içerir.
                  Aradığınız içeriğe hızlıca ulaşmak için kategorilere göz atabilirsiniz.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SiteMap;
